import { ApplicationFileRecord } from "models/view-models/aen-applications/application-file-record";
import {
    InstructorApplicationFilePathParams,
    InstructorApplicationFileService,
} from "utilities/services/aen-applications/instructor-application-file-service";
import { InstructorApplicationFileRecord } from "models/view-models/aen-applications/instructor-application-file-record";
import { InstructorApplicationFileType } from "models/enumerations/aen-applications/instructor-application-file-type";
import { NumberUtils } from "utilities/number-utils";
import { ResultErrorRecord } from "andculturecode-javascript-core";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization/t";
import { useCallback, useEffect, useState } from "react";

interface UseAenInstructorApplicationFilesHook {
    errors?: ResultErrorRecord[];
    files: InstructorApplicationFileRecord[];
    isLoading: boolean;
    saveFile: (aenApplicationFile: ApplicationFileRecord) => Promise<boolean>;
}

interface UseAenInstructorApplicationFilesHookOptions {
    aenApplicationId: number;
}

export function useAenInstructorApplicationFiles(
    props: UseAenInstructorApplicationFilesHookOptions
): UseAenInstructorApplicationFilesHook {
    const [isLoading, setIsLoading] = useState(true);
    const [files, setFiles] = useState<InstructorApplicationFileRecord[]>([]);
    const [errors, setErrors] = useState<ResultErrorRecord[]>();
    const { create: instructorApplicationFileCreate } =
        InstructorApplicationFileService.useCreate();
    const { list: listInstructorApplicationFiles } = InstructorApplicationFileService.useList();
    const aenApplicationId = NumberUtils.parseInt(props.aenApplicationId) ?? 0;

    const fetchData = useCallback(async () => {
        if (isNaN(aenApplicationId) || aenApplicationId < 1) {
            return;
        }

        try {
            (async function getInstructorApplicationFiles() {
                const listInstructorApplicationFilesPathParams: InstructorApplicationFilePathParams =
                    {
                        instructorApplicationId: aenApplicationId,
                    };
                const { result, resultObjects } = await listInstructorApplicationFiles(
                    listInstructorApplicationFilesPathParams
                );

                setIsLoading(false);

                if (result?.hasErrors()) {
                    const { errors } = result ?? {};
                    setErrors(errors ?? []);
                    return;
                }

                setFiles(resultObjects);
            })();
        } catch {
            ToastManager.error(t("thereWasAnIssueReturningApplicationFiles"));
        }
    }, [aenApplicationId, listInstructorApplicationFiles]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const saveFile = useCallback(
        async (aenApplicationFile: ApplicationFileRecord) => {
            try {
                const fileType = aenApplicationFile.type as InstructorApplicationFileType;
                const file = new InstructorApplicationFileRecord({
                    description: aenApplicationFile.description,
                    file: aenApplicationFile.file,
                    fileId: aenApplicationFile.fileId,
                    instructorApplicationId: aenApplicationId,
                    type: fileType,
                });
                const createInstructorApplicationFilePathParams: InstructorApplicationFilePathParams =
                    {
                        instructorApplicationId: aenApplicationId,
                    };

                const createFileResponse = await instructorApplicationFileCreate(
                    file,
                    createInstructorApplicationFilePathParams
                );
                const createFileResult = createFileResponse.result;
                if (createFileResult?.resultObject == null || createFileResult.hasErrors()) {
                    throw new Error();
                }

                fetchData();
                return true;
            } catch {
                ToastManager.error(t("thereWasAnIssueSavingTheFile"));
                return false;
            }
        },
        [aenApplicationId, fetchData, instructorApplicationFileCreate]
    );

    return {
        errors,
        files,
        isLoading,
        saveFile,
    };
}
